import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import ScreenRenderer, { fetchScreen } from '../components/sim/ScreenRenderer';
import SimScreenSkeleton from '../components/SimScreenSkeleton';
import SimButton from '../components/ui/SimButton';
import SimShell from '../sim/ui/SimShell';
import SimCard from '../sim/ui/SimCard';
import { SimBody, SimLabel, SimTitle } from '../sim/ui/SimText';
import { simTokens } from '../sim/ui/tokens';

type SimulationResponse = {
  simulation: {
    id: string;
    name: string;
    roleFamily: string;
    version: string;
  };
  variant: {
    id: string;
    variantCode: string;
    variantName: string;
  };
  screen: {
    id: string;
    screenCode: string;
    screenName: string;
    screenOrder: number;
    timeLimitSeconds: number;
  };
  components: Array<{
    rowKey: string;
    rowOrder: number;
    rowType: string;
    payload: Record<string, unknown>;
  }>;
};

const SimDevScreenPage: React.FC = () => {
  const { variantCode, screenCode } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<SimulationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const headerLabel = useMemo(() => {
    if (!data) return 'Simulation';
    return `${data.simulation.name} • ${data.variant.variantCode}`;
  }, [data]);

  useEffect(() => {
    const loadScreen = async () => {
      if (!variantCode || !screenCode) {
        setError('Missing simulation parameters.');
        setLoading(false);
        return;
      }
      try {
        const payload = await fetchScreen({
          variantCode,
          screenCode,
        });
        setData(payload as SimulationResponse);
      } catch (err: any) {
        console.error('Failed to load simulation screen:', err);
        setError('Failed to load simulation screen.');
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    setError(null);
    loadScreen();
  }, [variantCode, screenCode]);

  if (loading) {
    return <SimScreenSkeleton />;
  }

  if (error || !data) {
    return (
      <SimShell maxWidthClassName="max-w-3xl" className="flex items-center">
        <SimCard className="text-center space-y-4">
          <SimLabel className="text-rose-300">Error</SimLabel>
          <SimTitle className="text-xl sm:text-2xl">Couldn’t load this screen</SimTitle>
          <SimBody>Please refresh or try again.</SimBody>
          <div className="mt-6 flex justify-center">
            <SimButton
              onClick={() => {
                setLoading(true);
                setError(null);
              }}
            >
              Retry
            </SimButton>
          </div>
        </SimCard>
      </SimShell>
    );
  }

  const handleNext = () => {
    if (data.screen.screenCode === 'S0') {
      navigate(`/sim/dev/${data.variant.variantCode}/S1`);
      return;
    }
    toast.success('Done');
  };

  return (
    <SimShell
      maxWidthClassName="max-w-5xl"
      fixedAction={
        <SimButton onClick={handleNext} className="w-full sm:w-auto">
          {data.screen.screenCode === 'S1' ? 'Submit' : 'Next'}
        </SimButton>
      }
      header={
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className={simTokens.headerAccentLabel}>Simulation</p>
            <p className="text-lg font-semibold text-txt-primary">{headerLabel}</p>
            <p className="mt-1 text-sm text-txt-muted">{data.screen.screenName}</p>
          </div>
          <div className="flex items-center gap-3 text-sm text-txt-secondary">
            <span>Screen {data.screen.screenOrder + 1} of 2</span>
            <span className="inline-flex items-center rounded-full border border-line bg-surface-card px-3 py-1 text-xs uppercase tracking-[0.2em] text-txt-secondary">
              03:20
            </span>
          </div>
          <div className="text-xs text-txt-muted uppercase tracking-[0.2em]">
            Candidate: ____ • ID: ____
          </div>
        </div>
      }
    >
      <div className="mx-auto max-w-3xl">
        <SimCard>
          <div className="space-y-4">
            <SimLabel>Screen {data.screen.screenOrder + 1}</SimLabel>
            <SimTitle className="text-xl sm:text-2xl">{data.screen.screenName}</SimTitle>
            <ScreenRenderer components={data.components} />
          </div>
        </SimCard>
      </div>
    </SimShell>
  );
};

export default SimDevScreenPage;
